import React from 'react';
import {
  Alert,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Link,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';
import { Box } from '@mui/system';
import StudentRepository from '../services/StudentRepository.js';
import OperationModal from '../components/common/OperationModal.jsx';

export default function VerificationForm({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  setStudentCollege,
  onSkip,
}) {
  const manualEntryDefaults = {
    first_name: null,
    middle_name: null,
    surname: null,
    birthdate: null,
    studid: null,
    sex: null,
    degree: null,
    year_graduated: null,
  };
  const [loading, setLoading] = React.useState(false);
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = React.useState(false);
  const handleNext = async () => {
    setLoading(true);
    repo
      .getStudentRecords(formValues.studid, formValues.mom_maiden_name, formValues.birthdate)
      .then((studData) => {
        setLoading(false);
        setStudentCollege(studData.college);
        setFormValues({
          ...formValues,
          ...{
            is_first_gen_graduate: studData.is_first_gen_graduate,
          },
        });
        return repo.getAlumniRecord();
      })
      .then((data) => {
        setFormValues({
          ...formValues,
          ...{
            civil_status: data.civilstatus.toLowerCase(),
            gender: data.gender.trim(),
            pa_unit: data.ha_lbh_no,
            pa_street: data.ha_street,
            pa_brgy: data.ha_barangay,
            pa_city: data.ha_city,
            pa_province: data.ha_province,
            pa_country: data.ha_country,
            social_media: data.facebook_url,
            email: data.email,
            contact_number: data.mobilenum,
            months_before_first_job: data.months_before_work,
            is_first_gen_graduate: data.is_first_gen_graduate,
            is_employed_two_yrs_postgrad: data.is_employed_two_yrs_postgrad,
            is_studying_two_yrs_postgrad: data.is_studying_two_yrs_postgrad,
          },
        });
        setLoading(false);
        handleScreenNext();
      })
      .catch((error) => {
        console.error(error.message);
        const errorAlert = (
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We could not find your records. You may have not signed up at the Alumni Registry or
              your details do not match. Please try again or sign up at the{' '}
              <Link
                color={'#63f1ff'}
                href="http://x4150my.msuiit.edu.ph/my/v2/student/graduation-applications.php"
              >
                Graduation Application
              </Link>{' '}
              in My.IIT.
            </Typography>
          </Alert>
        );
        setLoading(false);
        handleErrors(errorAlert);
      });
  };
  const handleScreenNextManual = () => {
    setLoading(false);
    handleScreenNext();
  };
  const addManualEntry = async () => {
    setLoading(true);
    repo
      .addManualEntry({
        first_name: formValues.first_name,
        middle_name: formValues.middle_name,
        surname: formValues.surname,
        birthdate: formValues.birthdate,
        studid: formValues.studid,
        sex: formValues.sex,
        degree: formValues.degree,
        year_graduated: formValues.year_graduated,
      })
      .then(() => {
        setLoading(false);
        handleScreenNext();
      })
      .catch(() => {
        setLoading(false);
        handleErrors(
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We encountered a problem saving your details. Please contact the administrator at
              webteam@g.msuiit.edu.ph.
            </Typography>
          </Alert>
        );
      });
  };
  const isShiftable = () => {
    let notNullCount = 0;
    Object.keys(formValues).forEach((formKey) => {
      if (formValues[formKey] !== null) notNullCount = notNullCount + 1;
    });
    return notNullCount >= 2 && !loading;
  };
  const handleValueChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  return (
    <React.Fragment>
      <OperationModal
        open={isManualEntryModalOpen}
        canSubmit={
          Boolean(formValues.first_name) &&
          Boolean(formValues.middle_name) &&
          Boolean(formValues.surname) &&
          Boolean(formValues.birthdate) &&
          Boolean(formValues.sex) &&
          Boolean(formValues.degree) &&
          Boolean(formValues.year_graduated)
        }
        setIsModalOpen={setIsManualEntryModalOpen}
        onSubmit={handleScreenNextManual}
        formValues={formValues}
        defaultValues={manualEntryDefaults}
        setFormValues={setFormValues}
        operation={addManualEntry}
      >
        <TextField
          required
          id="first_name"
          name="first_name"
          label="First Name"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.first_name ? formValues.first_name : ''}
          fullWidth
        />
        <TextField
          required
          id="middle_name"
          name="middle_name"
          label="Middle Name"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.middle_name ? formValues.middle_name : ''}
          fullWidth
        />
        <TextField
          required
          id="surname"
          name="surname"
          label="Surname (upon graduation)"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.surname ? formValues.surname : ''}
          fullWidth
        />
        <TextField
          required
          id="birthdate"
          name="birthdate"
          type="date"
          label="Date of Birth"
          variant="outlined"
          onChange={handleValueChange}
          InputLabelProps={{
            shrink: true,
          }}
          fullWidth
        />
        <TextField
          required
          id="studid"
          name="studid"
          label="Student ID"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.studid ? formValues.studid : ''}
          fullWidth
        />
        <FormControl required fullWidth>
          <FormLabel id="sex-label">Sex</FormLabel>
          <RadioGroup
            aria-labelledby="sex-label"
            id="sex"
            className="subvariant-nobreakpoint"
            name="sex"
            value={formValues.sex ? formValues.sex : ''}
            label="Sex"
            onChange={handleValueChange}
          >
            <FormControlLabel value="female" control={<Radio />} label="Female" />
            <FormControlLabel value="male" control={<Radio />} label="Male" />
          </RadioGroup>
        </FormControl>
        <TextField
          required
          id="degree"
          name="degree"
          label="Degree Earned"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.degree ? formValues.degree : ''}
          fullWidth
        />
        <TextField
          required
          type={'number'}
          id="year_graduated"
          name="year_graduated"
          label="Year Graduated"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.year_graduated ? formValues.year_graduated : ''}
          fullWidth
        />
      </OperationModal>
      <TextField
        required
        id="studid"
        name="studid"
        label="Student ID No."
        variant="outlined"
        onChange={handleValueChange}
        value={formValues.studid ? formValues.studid : ''}
        fullWidth
      />
      <TextField
        required
        id="mom_maiden_name"
        name="mom_maiden_name"
        label="Mother's Maiden Name"
        variant="outlined"
        onChange={handleValueChange}
        value={formValues.mom_maiden_name ? formValues.mom_maiden_name : ''}
        fullWidth
      />
      <TextField
        required
        id="birthdate"
        name="birthdate"
        type="date"
        label="Date of Birth"
        variant="outlined"
        onChange={handleValueChange}
        value={formValues.birthdate ? formValues.birthdate : ''}
        InputLabelProps={{
          shrink: true,
        }}
        fullWidth
      />
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          {onSkip && (
            <Button
              variant="outlined"
              size="large"
              color="primary"
              onClick={onSkip}
              sx={{ marginRight: '10px' }}
            >
              Skip to Finish
            </Button>
          )}
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={!isShiftable()}
              onClick={handleNext}
            >
              Next
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                color={'success'}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

VerificationForm.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleErrors: PropTypes.func,
  setStudentId: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  setStudentCollege: PropTypes.func,
  onSkip: PropTypes.func,
};
